// web/frontend/src/pages/admin/AdminUserDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';

import {
  ArrowLeft, Mail, Building, Calendar, Shield, CreditCard,
  Activity, Search, Star, TrendingUp, Clock, Briefcase,
  Key, Trash2, Edit3, CheckCircle, XCircle, AlertTriangle,
  RefreshCw, UserCheck, Zap, Lock, Unlock, Copy, Check,
  MessageSquare, BarChart3, Smartphone, Eye, EyeOff,
  DollarSign, FileText, History,
  RotateCcw
} from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { SkeletonText, SkeletonCard } from '../../components/Skeleton';

export default function AdminUserDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentAdmin } = useAuth();

  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [resetUsageConfirm, setResetUsageConfirm] = useState(false);
  const [resetUsageText, setResetUsageText] = useState('');
  const [resetUsageLoading, setResetUsageLoading] = useState(false);
  
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const isSuperAdmin = currentAdmin?.role === 'super_admin';
  const isSelf = parseInt(userId) === currentAdmin?.id;
  const canView = isSuperAdmin || (!isSuperAdmin && userData?.user?.role !== 'super_admin');

  useEffect(() => {
    loadUserDetails();
  }, [userId]);

  const loadUserDetails = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/admin/users/${userId}`);
      const data = res.data;
      
      // Block viewing super admin profiles for non-super admins
      if (data.user?.role === 'super_admin' && !isSuperAdmin) {
        toast.error('Access denied');
        navigate('/dashboard/admin/users');
        return;
      }
      
      setUserData(data);
      setEditForm({
        full_name: data.user?.full_name || '',
        email: data.user?.email || '',
        company_name: data.user?.company_name || '',
        role: data.user?.role || 'user',
        plan: data.user?.plan || 'free',
        subscription_status: data.user?.subscription_status || 'active',
      });
    } catch (err) {
      toast.error('Failed to load user details');
      navigate('/dashboard/admin/users');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await api.put(`/api/admin/users/${userId}`, editForm);
      toast.success('User updated');
      setEditMode(false);
      loadUserDetails();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleAction = async (action) => {
    setConfirmAction(null);
    setActionLoading(action);
    try {
      // Route to the correct endpoint based on action type
      if (action === 'cancel-subscription') {
        await api.post(`/api/admin/users/${userId}/cancel-subscription`);
      } else if (action === 'reactivate-subscription') {
        await api.post(`/api/admin/users/${userId}/reactivate-subscription`);
      } else if (action === 'reset-account') {
        await api.post(`/api/admin/users/${userId}/reset-account`);
      } else {
        await api.post(`/api/admin/users/${userId}/actions`, { action });
      }
      toast.success(getActionMessage(action));
      loadUserDetails();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(null);
    }
  };

  const getActionMessage = (action) => ({
    'reset-password': 'Password reset email sent',
    'reset-account': 'Account reset successfully',
    'verify-email': 'Email verified',
    'clear-sessions': 'All sessions cleared',
    'toggle-onboarding': 'Onboarding updated',
    'cancel-subscription': 'Subscription cancelled',
    'reactivate-subscription': 'Subscription reactivated',
  })[action] || 'Done';

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/api/admin/users/${userId}`);
      toast.success('User deleted successfully');
      setShowDeleteConfirm(false);
      setDeleteConfirmText('');
      navigate('/dashboard/admin/users');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setAddingNote(true);
    try {
      await api.post(`/api/admin/users/${userId}/notes`, { note: newNote });
      setNewNote('');
      toast.success('Note added');
      loadUserDetails();
    } catch {
      toast.error('Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  const handleToggleLock = async () => {
    setActionLoading('toggle-lock');
    try {
      await api.post(`/api/admin/users/${userId}/toggle-lock`);
      toast.success(userData?.user?.is_locked ? 'Account unlocked' : 'Account locked');
      loadUserDetails();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await api.delete(`/api/admin/users/${userId}/notes/${noteId}`);
      toast.success('Note deleted');
      loadUserDetails();
    } catch {
      toast.error('Failed to delete note');
    }
  };

  if (!userData) return null;
  
  const { user, twofa_enabled, session_count, last_active, usage, notes, adjustments, crypto_payments } = userData;
  
  // Safe defaults
  const cryptoPayments = crypto_payments || [];
  
  // Usage values with safe defaults
  const searchesUsed = usage?.searches?.used ?? 0;
  const searchesLimit = usage?.searches?.limit ?? 3;
  const aiScoresUsed = usage?.ai_scores?.used ?? 0;
  const aiScoresLimit = usage?.ai_scores?.limit ?? 20;
  const totalLeads = usage?.total_leads ?? 0;
  const scoredLeads = usage?.scored_leads ?? 0;
  const hotLeads = usage?.hot_leads ?? 0;
  const avgScore = usage?.avg_score ?? 0;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getInitials = (name, email) => {
    if (name) return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
    return (email || 'U').split('@')[0].slice(0, 2).toUpperCase();
  };

  if (loading) {
    return (
      <div className="page-content" style={{ maxWidth: '100%' }}>
        <SkeletonText width={120} height={16} style={{ marginBottom: 20 }} />
        <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
          <SkeletonText width={72} height={72} style={{ borderRadius: 16 }} />
          <div style={{ flex: 1 }}><SkeletonText width={200} height={24} /><SkeletonText width={150} height={14} style={{ marginTop: 8 }} /></div>
        </div>
        <SkeletonCard height={200} />
      </div>
    );
  }

  if (!userData || !canView) return null;


  const roleBadgeColors = {
    super_admin: { bg: 'rgba(245,158,11,0.15)', color: '#f59e0b', label: 'Super Admin' },
    admin: { bg: 'rgba(124,58,237,0.15)', color: '#7c3aed', label: 'Admin' },
    user: { bg: 'var(--bg-tertiary)', color: 'var(--text-secondary)', label: 'User' },
  };
  const roleBadge = roleBadgeColors[user?.role] || roleBadgeColors.user;

  const planColors = {
    free: 'var(--text-secondary)', growth: 'var(--accent-primary)',
    pro: 'var(--accent-secondary)', enterprise: '#f59e0b',
  };

  return (
    <div className="page-content" style={{ maxWidth: '100%' }}>
      {/* Admin Login As User Bar - only for regular users */}
      {userData?.user?.role === 'user' && (
      <div style={{
        display: 'flex', gap: 10, marginBottom: 16,
        background: 'var(--accent-glow)',
        border: '1px solid var(--accent-primary)',
        borderRadius: 'var(--radius-lg)',
        padding: '10px 16px',
        alignItems: 'center',
      }}>
        <span style={{ color: 'var(--accent-primary)', fontSize: 13, fontWeight: 600 }}>
          👤 Admin Tools:
        </span>
        <button
          onClick={async () => {
            try {
              const res = await api.post(`/api/admin/users/${userId}/login-as`);
              localStorage.setItem('access_token', res.data.access_token);
              localStorage.setItem('refresh_token', res.data.refresh_token);
              localStorage.setItem('user_email', res.data.user_email);
              localStorage.setItem('user_full_name', res.data.user_name || '');
              localStorage.setItem('user_id', res.data.user_id || '');
              toast.success(`Logged in as ${res.data.user_email}`);
              setTimeout(() => {
                window.location.href = '/dashboard';
              }, 500);
            } catch (err) {
              toast.error(err.response?.data?.detail || 'Failed to login as user');
            }
          }}
          style={{
            padding: '6px 14px', borderRadius: 6,
            background: 'var(--accent-primary)', border: 'none',
            color: '#fff', cursor: 'pointer',
            fontSize: 12, fontWeight: 600,
          }}
        >
          Login As User
        </button>
        <span style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>
          You will be redirected as this user. Use logout to return.
        </span>
      </div>

      
      )}

      {/* Back Button + Actions Row */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        flexWrap: 'wrap', gap: 8, marginBottom: 'var(--space-lg)',
      }}>
        <button onClick={() => navigate('/dashboard/admin/users')} style={{
          background: 'none', border: 'none', color: 'var(--text-secondary)',
          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
          fontSize: 13, padding: 0,
        }}>
          <ArrowLeft size={16} /> Back to Users
        </button>
      
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <ActionChip icon={<RotateCcw size={13} />} label="Reset Account" color="var(--info)"
              onClick={() => setConfirmAction({ action: 'reset-account', title: 'Reset Account', message: `Reset ${user?.email}? This verifies email, completes onboarding, unlocks account, and removes 2FA.`, confirmLabel: 'Reset Account' })}
              loading={actionLoading === 'reset-account'} />
          {!isSelf && (
            <ActionChip icon={<Key size={13} />} label="Reset PW" color="var(--warning)" 
              onClick={() => setConfirmAction({ action: 'reset-password', title: 'Reset Password', message: `Send password reset email to ${user?.email}?`, confirmLabel: 'Send Reset Email' })} 
              loading={actionLoading === 'reset-password'} />
          )}
          {!user?.email_verified && (
            <ActionChip icon={<CheckCircle size={13} />} label="Verify Email" color="var(--success)" 
              onClick={() => handleAction('verify-email')} 
              loading={actionLoading === 'verify-email'} />
          )}
          {!isSelf && (
            <ActionChip icon={<Shield size={13} />} label="Clear Sessions" color="var(--info)" 
              onClick={() => setConfirmAction({ action: 'clear-sessions', title: 'Clear All Sessions', message: `Force logout on all devices for ${user?.email}?`, confirmLabel: 'Clear Sessions' })} 
              loading={actionLoading === 'clear-sessions'} />
          )}
          {user?.role !== 'super_admin' && (isSuperAdmin || user?.role !== 'admin') && !isSelf && (
            <ActionChip icon={user?.is_locked ? <Lock size={13} /> : <Unlock size={13} />} 
              label={user?.is_locked ? 'Unlock' : 'Lock'} 
              color={user?.is_locked ? 'var(--success)' : 'var(--error)'} 
              onClick={() => handleToggleLock()} 
              loading={actionLoading === 'toggle-lock'} />
          )}
          {isSuperAdmin && (
            <ActionChip icon={editMode ? <XCircle size={13} /> : <Edit3 size={13} />} 
              label={editMode ? 'Cancel' : 'Edit'} color="var(--accent-primary)" 
              onClick={() => setEditMode(!editMode)} />
          )}
          {isSuperAdmin && user?.role !== 'super_admin' && (
            <ActionChip icon={<Trash2 size={13} />} label="Delete" color="var(--error)" 
              onClick={() => setShowDeleteConfirm(true)} />
          )}
          {isSuperAdmin && user?.plan !== 'free' && user?.subscription_status === 'active' && (
            <ActionChip icon={<XCircle size={13} />} label="Cancel Sub" color="var(--warning)" 
              onClick={() => setConfirmAction({ action: 'cancel-subscription', title: 'Cancel Subscription', message: "Cancel " + user?.email + "'s " + (user?.plan?.toUpperCase() || '') + " subscription? Access remains until period end, then account becomes inactive.", confirmLabel: 'Cancel Subscription' })} 
              loading={actionLoading === 'cancel-subscription'} />
          )}
          {isSuperAdmin && user?.subscription_status === 'cancelled' && (
            <ActionChip icon={<RefreshCw size={13} />} label="Reactivate" color="var(--success)" 
              onClick={() => setConfirmAction({ action: 'reactivate-subscription', title: 'Reactivate Subscription', message: "Reactivate " + user?.email + "'s " + (user?.plan?.toUpperCase() || '') + " subscription? Billing will resume.", confirmLabel: 'Reactivate' })} 
              loading={actionLoading === 'reactivate-subscription'} />
          )}
        </div>
      </div>
      
      {/* Render modals directly to document.body via Portal */}
      {confirmAction && createPortal(
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 99999,
        }} onClick={() => setConfirmAction(null)}>
          <div style={{
            background: 'var(--bg-secondary)', borderRadius: 16, padding: '24px 28px',
            maxWidth: 420, width: '90%', border: '1px solid var(--border-primary)',
            boxShadow: 'var(--shadow-xl)',
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ color: 'var(--text-primary)', fontSize: 16, fontWeight: 700, margin: '0 0 8px' }}>
              {confirmAction.title}
            </h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: '0 0 20px', lineHeight: 1.5 }}>
              {confirmAction.message}
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setConfirmAction(null)} style={{
                flex: 1, padding: '12px', borderRadius: 10,
                border: '1px solid var(--border-primary)', background: 'transparent',
                color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 14, fontWeight: 600,
              }}>Cancel</button>
              <button onClick={() => { setConfirmAction(null); handleAction(confirmAction.action); }} style={{
                flex: 1, padding: '12px', borderRadius: 10, border: 'none',
                background: 'var(--accent-gradient)', color: 'white',
                cursor: 'pointer', fontSize: 14, fontWeight: 700,
              }}>{confirmAction.confirmLabel || 'Confirm'}</button>
            </div>
          </div>
        </div>,
        document.body
      )}
      
      {showDeleteConfirm && createPortal(
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 99999,
        }} onClick={() => setShowDeleteConfirm(false)}>
          <div style={{
            background: 'var(--bg-secondary)', borderRadius: 16, padding: '24px 28px',
            maxWidth: 440, width: '90%', border: '1px solid var(--error)',
            boxShadow: 'var(--shadow-xl)',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <AlertTriangle size={40} color="var(--error)" style={{ marginBottom: 12 }} />
              <h3 style={{ color: 'var(--error)', fontSize: 18, fontWeight: 700, margin: '0 0 8px' }}>
                Delete User Account?
              </h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: 0, lineHeight: 1.5 }}>
                This will permanently delete <strong>{user?.email}</strong> and all associated data. This action cannot be undone.
              </p>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowDeleteConfirm(false)} style={{
                flex: 1, padding: '12px', borderRadius: 10,
                border: '1px solid var(--border-primary)', background: 'transparent',
                color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 14, fontWeight: 600,
              }}>Cancel</button>
              <button onClick={handleDelete} disabled={deleting} style={{
                flex: 1, padding: '12px', borderRadius: 10, border: 'none',
                background: 'var(--error)', color: 'white',
                cursor: 'pointer', fontSize: 14, fontWeight: 700,
                opacity: deleting ? 0.7 : 1,
              }}>{deleting ? 'Deleting...' : 'Delete Forever'}</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Reset Usage Confirmation Modal */}
      {resetUsageConfirm && createPortal(
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 99999,
        }} onClick={() => { setResetUsageConfirm(false); setResetUsageText(''); }}>
          <div style={{
            background: 'var(--bg-secondary)', borderRadius: 16, padding: '24px 28px',
            maxWidth: 460, width: '90%', border: '1px solid var(--warning)',
            boxShadow: 'var(--shadow-xl)',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <AlertTriangle size={40} color="var(--warning)" style={{ marginBottom: 12 }} />
              <h3 style={{ color: 'var(--warning)', fontSize: 18, fontWeight: 700, margin: '0 0 8px' }}>
                Reset Monthly Usage?
              </h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: 0, lineHeight: 1.5 }}>
                This will reset all monthly usage counters for <strong>{user?.email}</strong> to zero.
                They will be able to run searches and use AI scoring again this month.
              </p>
            </div>
            
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                Type <strong style={{ color: 'var(--warning)' }}>RESET</strong> to confirm:
              </label>
              <input 
                value={resetUsageText}
                onChange={(e) => setResetUsageText(e.target.value)}
                placeholder="Type RESET"
                style={{
                  width: '100%', padding: '10px 14px', borderRadius: 10,
                  border: '1px solid var(--border-primary)', background: 'var(--input-bg)',
                  color: 'var(--text-primary)', fontSize: 14, outline: 'none',
                  textAlign: 'center', letterSpacing: 4, fontWeight: 700, boxSizing: 'border-box'
                }}
                autoFocus
              />
            </div>
      
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => { setResetUsageConfirm(false); setResetUsageText(''); }} style={{
                flex: 1, padding: '12px', borderRadius: 10,
                border: '1px solid var(--border-primary)', background: 'transparent',
                color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 14, fontWeight: 600,
              }}>Cancel</button>
              <button 
                onClick={async () => {
                  setResetUsageLoading(true);
                  try {
                    await api.post(`/api/admin/billing/reset-usage?user_id=${userId}`);
                    toast.success('Monthly usage reset successfully');
                    setResetUsageConfirm(false);
                    setResetUsageText('');
                    loadUserDetails();
                  } catch {
                    toast.error('Failed to reset usage');
                  } finally {
                    setResetUsageLoading(false);
                  }
                }}
                disabled={resetUsageText !== 'RESET' || resetUsageLoading}
                style={{
                  flex: 1, padding: '12px', borderRadius: 10, border: 'none',
                  background: resetUsageText === 'RESET' ? 'var(--warning)' : 'var(--bg-tertiary)',
                  color: resetUsageText === 'RESET' ? 'white' : 'var(--text-tertiary)',
                  cursor: resetUsageText === 'RESET' ? 'pointer' : 'not-allowed',
                  fontSize: 14, fontWeight: 700,
                  opacity: resetUsageLoading ? 0.7 : 1,
                }}>
                {resetUsageLoading ? 'Resetting...' : 'Reset Usage'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      
      {/* Delete Confirmation Modal,  Updated with text verification */}
      {showDeleteConfirm && createPortal(
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 99999,
        }} onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); }}>
          <div style={{
            background: 'var(--bg-secondary)', borderRadius: 16, padding: '24px 28px',
            maxWidth: 460, width: '90%', border: '1px solid var(--error)',
            boxShadow: 'var(--shadow-xl)',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <AlertTriangle size={40} color="var(--error)" style={{ marginBottom: 12 }} />
              <h3 style={{ color: 'var(--error)', fontSize: 18, fontWeight: 700, margin: '0 0 8px' }}>
                Delete User Account?
              </h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: 0, lineHeight: 1.5 }}>
                This will permanently delete <strong>{user?.email}</strong> and ALL associated data including leads, searches, and settings. This action cannot be undone.
              </p>
            </div>
            
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                Type <strong style={{ color: 'var(--error)' }}>DELETE</strong> to confirm:
              </label>
              <input 
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type DELETE"
                style={{
                  width: '100%', padding: '10px 14px', borderRadius: 10,
                  border: '1px solid var(--border-primary)', background: 'var(--input-bg)',
                  color: 'var(--text-primary)', fontSize: 14, outline: 'none',
                  textAlign: 'center', letterSpacing: 4, fontWeight: 700, boxSizing: 'border-box'
                }}
                autoFocus
              />
            </div>
      
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); }} style={{
                flex: 1, padding: '12px', borderRadius: 10,
                border: '1px solid var(--border-primary)', background: 'transparent',
                color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 14, fontWeight: 600,
              }}>Cancel</button>
              <button 
                onClick={handleDelete}
                disabled={deleteConfirmText !== 'DELETE' || deleting}
                style={{
                  flex: 1, padding: '12px', borderRadius: 10, border: 'none',
                  background: deleteConfirmText === 'DELETE' ? 'var(--error)' : 'var(--bg-tertiary)',
                  color: deleteConfirmText === 'DELETE' ? 'white' : 'var(--text-tertiary)',
                  cursor: deleteConfirmText === 'DELETE' ? 'pointer' : 'not-allowed',
                  fontSize: 14, fontWeight: 700,
                  opacity: deleting ? 0.7 : 1,
                }}>
                {deleting ? 'Deleting...' : 'Delete Forever'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Hero Card */}
      <div style={s.card}>
        <div style={{ display: 'flex', gap: 'var(--space-lg)', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={s.avatar}>{getInitials(user?.full_name, user?.email)}</div>
          <div style={{ flex: 1, minWidth: 200 }}>
            {editMode ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
                <InputField label="Full Name" value={editForm.full_name} onChange={(v) => setEditForm({...editForm, full_name: v})} />
                <InputField label="Email" value={editForm.email} onChange={(v) => setEditForm({...editForm, email: v})} />
                <InputField label="Company" value={editForm.company_name} onChange={(v) => setEditForm({...editForm, company_name: v})} />
                <SelectField label="Role" value={editForm.role} onChange={(v) => setEditForm({...editForm, role: v})} options={[{value:'user',label:'User'},{value:'admin',label:'Admin'}]} />
                <SelectField label="Plan" value={editForm.plan} onChange={(v) => setEditForm({...editForm, plan: v})} options={[{value:'free',label:'Free'},{value:'starter',label:'Starter'},{value:'growth',label:'Growth'},{value:'pro',label:'Pro'},{value:'enterprise',label:'Enterprise'}]} />
                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <button onClick={handleSaveEdit} disabled={saving} style={s.saveBtn}>{saving ? 'Saving...' : 'Save Changes'}</button>
                </div>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                  <h2 style={{ color: 'var(--text-primary)', fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>{user?.full_name || 'No Name'}</h2>
                  <Badge bg={roleBadge.bg} color={roleBadge.color}>{roleBadge.label}</Badge>
                  <Badge bg={`${planColors[user?.plan] || '#666'}15`} color={planColors[user?.plan] || '#666'} style={{textTransform:'capitalize'}}>{user?.plan || 'free'}</Badge>
                  {user?.email_verified ? <Badge bg="var(--success-bg)" color="var(--success)"><CheckCircle size={10} /> Verified</Badge> : <Badge bg="var(--error-bg)" color="var(--error)"><XCircle size={10} /> Unverified</Badge>}
                  {twofa_enabled && <Badge bg="rgba(124,58,237,0.1)" color="#7c3aed"><Lock size={10} /> 2FA</Badge>}
                  {isSelf && <Badge bg="var(--info-bg)" color="var(--info)">You</Badge>}
                  {user?.is_locked ? (
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '2px 8px', borderRadius: 'var(--radius-full)',
                        fontSize: 11, fontWeight: 600,
                        background: 'var(--error-bg)', color: 'var(--error)',
                      }}>
                      <Lock size={10} /> Locked
                    </span>
                  ) : null}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={s.infoRow}><Mail size={13} /> {user?.email}</span>
                  {user?.company_name && <span style={s.infoRow}><Building size={13} /> {user?.company_name}</span>}
                  {user?.service_name && <span style={s.infoRow}><Briefcase size={13} /> {user?.service_name}</span>}
                  <span style={{ ...s.infoRow, color: 'var(--text-tertiary)', fontSize: 12 }}><Calendar size={12} /> Joined {formatDate(user?.created_at)}</span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Stats Grid,  Live data from API */}
      <div style={s.statsGrid}>
        <MiniStat icon={<Search size={18} />} label="Total Leads" value={totalLeads} color="var(--info)" />
        <MiniStat icon={<Zap size={18} />} label="AI Scored" value={scoredLeads} color="var(--accent-secondary)" />
        <MiniStat icon={<Star size={18} />} label="Hot Leads" value={hotLeads} color="var(--error)" />
        <MiniStat icon={<TrendingUp size={18} />} label="Avg Score" value={avgScore ? Math.round(avgScore) : 0} color="var(--warning)" />
        <MiniStat icon={<Smartphone size={18} />} label="Sessions" value={session_count ?? 0} color="var(--success)" />
        <MiniStat icon={<Clock size={18} />} label="Last Active" value={last_active ? new Date(last_active).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Never'} color="var(--text-secondary)" />
      </div>

      {/* Tabs */}
      <div style={s.tabBar}>
        {[
          { id: 'overview', icon: <Activity size={14} />, label: 'Overview' },
          { id: 'usage', icon: <BarChart3 size={14} />, label: 'Usage' },
          { id: 'billing', icon: <DollarSign size={14} />, label: 'Billing' },
          { id: 'notes', icon: <MessageSquare size={14} />, label: `Notes (${notes?.length || 0})` },
          { id: 'emails', icon: <Mail size={14} />, label: 'Emails' },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={s.tab(activeTab === tab.id)}>
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* OVERVIEW TAB */}
      {activeTab === 'overview' && (
        <div style={s.cardGrid}>
          <div style={s.card}>
            <h3 style={s.cardTitle}><Shield size={16} /> Account Details</h3>
            <DetailRow icon={<Mail size={14} />} label="Email" value={user?.email} copyable />
            <DetailRow icon={<UserCheck size={14} />} label="Full Name" value={user?.full_name || 'Not set'} />
            <DetailRow icon={<Building size={14} />} label="Company" value={user?.company_name || 'Not set'} />
            <DetailRow icon={<Briefcase size={14} />} label="Service" value={user?.service_name || 'Not set'} />
            <DetailRow icon={<CreditCard size={14} />} label="Plan" value={user?.plan?.toUpperCase() || 'FREE'} />
            <DetailRow icon={<Calendar size={14} />} label="Joined" value={formatDate(user?.created_at)} />
            <DetailRow icon={<Clock size={14} />} label="Updated" value={formatDate(user?.updated_at)} />
          </div>
          <div style={s.card}>
            <h3 style={s.cardTitle}><Lock size={16} /> Security & Status</h3>
            <DetailRow icon={user?.email_verified ? <CheckCircle size={14} color="var(--success)" /> : <XCircle size={14} color="var(--error)" />} label="Email" value={user?.email_verified ? 'Verified' : 'Not Verified'} />
            <DetailRow icon={user?.onboarding_complete ? <CheckCircle size={14} color="var(--success)" /> : <XCircle size={14} color="var(--warning)" />} label="Onboarding" value={user?.onboarding_complete ? 'Complete' : 'Incomplete'} />
            <DetailRow icon={twofa_enabled ? <Lock size={14} color="var(--success)" /> : <Unlock size={14} color="var(--text-tertiary)" />} label="2FA" value={twofa_enabled ? 'Enabled' : 'Disabled'} />
            <DetailRow icon={<Smartphone size={14} />} label="Sessions" value={String(session_count ?? 0)} />
            <DetailRow icon={<Clock size={14} />} label="Last Active" value={last_active ? formatDate(last_active) : 'Never'} />
            <DetailRow icon={<Activity size={14} />} label="Subscription" value={user?.subscription_status?.toUpperCase() || 'ACTIVE'} />
          </div>
        </div>
      )}

      {/* USAGE TAB */}
      {activeTab === 'usage' && (
        <div style={s.card}>
          <h3 style={s.cardTitle}><BarChart3 size={16} /> Usage Statistics</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>
            Current billing period usage. Resets monthly.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 'var(--space-md)' }}>
            <UsageCard label="Searches" used={searchesUsed} limit={searchesLimit} color="var(--accent-primary)" />
            {user?.plan !== 'free' && (
              <p style={{ color: 'var(--text-tertiary)', fontSize: 11, marginTop: -8, marginBottom: 16 }}>
                ⓘ Plan default: {user?.plan === 'growth' ? '10' : user?.plan === 'pro' ? '20' : user?.plan === 'starter' ? '5' : '3'} searches/month. Extra credits are temporary and reset on plan change.
              </p>
            )}
            <UsageCard label="AI Scores" used={aiScoresUsed} limit={aiScoresLimit} color="var(--accent-secondary)" />
          </div>
          <div style={{ marginTop: 'var(--space-lg)' }}>
            <h4 style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Lead Stats</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 'var(--space-sm)' }}>
              <div style={s.statBox}><div style={{ fontSize: 24, fontWeight: 800, color: 'var(--info)' }}>{usage?.total_leads ?? 0}</div><div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Total Leads</div></div>
              <div style={s.statBox}><div style={{ fontSize: 24, fontWeight: 800, color: 'var(--accent-secondary)' }}>{usage?.scored_leads ?? 0}</div><div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>AI Scored</div></div>
              <div style={s.statBox}><div style={{ fontSize: 24, fontWeight: 800, color: 'var(--error)' }}>{usage?.hot_leads ?? 0}</div><div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Hot Leads</div></div>
              <div style={s.statBox}><div style={{ fontSize: 24, fontWeight: 800, color: 'var(--warning)' }}>{usage?.avg_score ? Math.round(usage.avg_score) : 0}</div><div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Avg Score</div></div>
            </div>
          </div>
          {isSuperAdmin && (
            <button 
              onClick={() => setResetUsageConfirm(true)}
              style={{
                padding: '10px 20px', borderRadius: 'var(--radius-md)',
                border: 'none', background: 'var(--warning)',
                color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 700,
                marginTop: 16, display: 'flex', alignItems: 'center', gap: 6,
              }}>
              <RefreshCw size={14} /> Reset Monthly Usage
            </button>
          )}
        </div>
      )}

      {/* BILLING TAB */}
      {activeTab === 'billing' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xl)' }}>
          
          {/* Current Plan Card */}
          <div className="billing-history-card">
            <h4 style={{ color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 14px', paddingBottom: 10, borderBottom: '1px solid var(--border-primary)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <CreditCard size={14} /> Current Plan
            </h4>
            
            {/* Subscription Status Banners */}
            {user?.subscription_status === 'cancelled' && (
              <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 14, background: 'var(--error-bg)', border: '1px solid var(--error)', display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                <AlertTriangle size={14} color="var(--error)" />
                <span style={{ color: 'var(--error)', fontWeight: 600 }}>Cancelled</span>
                <span style={{ color: 'var(--text-secondary)' }}>,  Access until {user?.current_period_end ? new Date(user.current_period_end).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'end of period'}</span>
              </div>
            )}
            {user?.plan === 'free' && userData?.trial_status === 'active' && (
              <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 14, background: 'var(--info-bg)', border: '1px solid var(--info)', display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                <Clock size={14} color="var(--info)" />
                <span style={{ color: 'var(--info)', fontWeight: 600 }}>7-Day Trial</span>
                <span style={{ color: 'var(--text-secondary)' }}>,  {userData?.trial_days_remaining} day{userData?.trial_days_remaining !== 1 ? 's' : ''} remaining</span>
              </div>
            )}
            {user?.plan === 'free' && userData?.trial_status === 'expired' && (
              <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 14, background: 'var(--warning-bg)', border: '1px solid var(--warning)', display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                <AlertTriangle size={14} color="var(--warning)" />
                <span style={{ color: 'var(--warning)', fontWeight: 600 }}>Trial Expired</span>
              </div>
            )}
            {user?.pending_plan && (
              <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 14, background: 'var(--info-bg)', border: '1px solid var(--info)', display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                <RefreshCw size={14} color="var(--info)" />
                <span style={{ color: 'var(--text-secondary)' }}>Scheduled downgrade to <strong style={{ color: 'var(--text-primary)' }}>{user?.pending_plan?.toUpperCase()}</strong> on {user?.current_period_end ? new Date(user.current_period_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'period end'}</span>
              </div>
            )}
            
            <table className="billing-table">
              <tbody>
                <tr><td style={{ padding: '8px 12px', color: 'var(--text-secondary)', fontSize: 12, width: 140 }}>Plan</td><td style={{ padding: '8px 12px', color: 'var(--text-primary)', fontWeight: 600, textTransform: 'capitalize' }}>{user?.plan || 'free'}</td></tr>
                <tr><td style={{ padding: '8px 12px', color: 'var(--text-secondary)', fontSize: 12 }}>Status</td><td style={{ padding: '8px 12px' }}><span style={{ padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: (user?.subscription_status || 'active') === 'active' ? 'var(--success-bg)' : (user?.subscription_status || 'active') === 'cancelled' ? 'var(--error-bg)' : 'var(--warning-bg)', color: (user?.subscription_status || 'active') === 'active' ? 'var(--success)' : (user?.subscription_status || 'active') === 'cancelled' ? 'var(--error)' : 'var(--warning)', textTransform: 'capitalize' }}>{user?.subscription_status || 'active'}</span></td></tr>
                <tr><td style={{ padding: '8px 12px', color: 'var(--text-secondary)', fontSize: 12 }}>Billing Cycle</td><td style={{ padding: '8px 12px', color: 'var(--text-primary)', textTransform: 'capitalize' }}>{user?.billing_cycle || 'monthly'}</td></tr>
                <tr><td style={{ padding: '8px 12px', color: 'var(--text-secondary)', fontSize: 12 }}>Lifetime Revenue</td><td style={{ padding: '8px 12px', color: 'var(--success)', fontWeight: 700 }}>${userData?.lifetime_revenue?.toFixed(2) || '0.00'}</td></tr>
                {user?.current_period_end && <tr><td style={{ padding: '8px 12px', color: 'var(--text-secondary)', fontSize: 12 }}>Period Ends</td><td style={{ padding: '8px 12px', color: 'var(--text-primary)' }}>{new Date(user.current_period_end).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</td></tr>}
              </tbody>
            </table>
          </div>

          {/* Payment History Card */}
          <div className="billing-history-card">
            <h4 style={{ color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 14px', paddingBottom: 10, borderBottom: '1px solid var(--border-primary)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <DollarSign size={14} /> Payment History
            </h4>
            
            {/* Flutterwave Payments */}
            {userData?.flutterwave_payments && userData.flutterwave_payments.length > 0 && (
              <>
                <h5 style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, margin: '0 0 8px' }}>💳 Card Payments</h5>
                <table className="billing-table" style={{ marginBottom: 16 }}>
                  <thead>
                    <tr>
                      <th>Plan</th><th>Cycle</th><th>Amount</th><th>Status</th><th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userData.flutterwave_payments.map((p, i) => (
                      <tr key={`flw-${i}`}>
                        <td style={{ textTransform: 'capitalize' }}>{p.plan}</td>
                        <td style={{ textTransform: 'capitalize' }}>{p.billing_cycle || 'monthly'}</td>
                        <td className="amount">${p.amount?.toFixed(2)}</td>
                        <td><span className={`status-badge ${p.status}`}>{p.status}</span></td>
                        <td style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{formatDate(p.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {/* Crypto Payments */}
            {cryptoPayments && cryptoPayments.length > 0 && (
              <>
                <h5 style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, margin: '0 0 8px' }}>₮ Crypto Payments</h5>
                <table className="billing-table" style={{ marginBottom: 16 }}>
                  <thead>
                    <tr>
                      <th>Plan</th><th>Currency</th><th>Amount</th><th>Status</th><th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cryptoPayments.map((p, i) => (
                      <tr key={`crypto-${i}`}>
                        <td style={{ textTransform: 'capitalize' }}>{p.plan}</td>
                        <td>{p.pay_currency?.toUpperCase()}</td>
                        <td className="amount">${p.amount_usd?.toFixed(2)}</td>
                        <td><span className={`status-badge ${p.payment_status}`}>{p.payment_status}</span></td>
                        <td style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{formatDate(p.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {(!userData?.flutterwave_payments || userData.flutterwave_payments.length === 0) && (!cryptoPayments || cryptoPayments.length === 0) && (
              <p className="billing-empty">No payment history.</p>
            )}
          </div>

          {/* Billing Adjustments Card */}
          <div className="billing-history-card">
            <h4 style={{ color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 14px', paddingBottom: 10, borderBottom: '1px solid var(--border-primary)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <FileText size={14} /> Adjustments ({adjustments?.length || 0})
            </h4>
            {adjustments && adjustments.length > 0 ? (
              <table className="billing-table">
                <thead>
                  <tr><th>Type</th><th>Reason</th><th style={{ textAlign: 'right' }}>Amount</th></tr>
                </thead>
                <tbody>
                  {adjustments.map((adj, i) => (
                    <tr key={i}>
                      <td><span style={{ color: adj.type === 'credit' ? 'var(--success)' : 'var(--error)', fontWeight: 600, textTransform: 'capitalize' }}>{adj.type}</span></td>
                      <td style={{ color: 'var(--text-secondary)' }}>{adj.reason || 'No reason'}</td>
                      <td className="amount">${adj.amount?.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="billing-empty">No billing adjustments.</p>
            )}
          </div>
        </div>
      )}

{activeTab === 'notes' && (
        <div style={s.card}>
          <h3 style={s.cardTitle}><MessageSquare size={16} /> Admin Notes</h3>
          <div style={{ marginBottom: 'var(--space-lg)' }}>
            <textarea value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Add a private note about this user..." rows={3} style={s.textarea} />
            <button onClick={handleAddNote} disabled={addingNote || !newNote.trim()} style={{ ...s.saveBtn, marginTop: 8, opacity: !newNote.trim() ? 0.5 : 1 }}>
              {addingNote ? 'Adding...' : 'Add Note'}
            </button>
          </div>

          {notes && notes.length > 0 ? (
            notes.map(note => (
              <div key={note.id} style={{ padding: 'var(--space-md)', background: 'var(--bg-tertiary)', borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-sm)', border: '1px solid var(--border-primary)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <p style={{ color: 'var(--text-primary)', fontSize: 13, margin: 0, whiteSpace: 'pre-wrap', flex: 1 }}>{note.note}</p>
                  <button onClick={() => handleDeleteNote(note.id)} style={s.deleteNoteBtn}><Trash2 size={12} /></button>
                </div>
                <div style={{ color: 'var(--text-tertiary)', fontSize: 10, marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span>By: {note.created_by_name || 'Unknown'}</span>
                  <span>·</span>
                  <span>{formatDate(note.created_at)}</span>
                </div>
              </div>
            ))
          ) : (
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, textAlign: 'center', padding: 20 }}>No notes yet.</p>
          )}
        </div>
      )}

      {/* EMAILS TAB */}
      {activeTab === 'emails' && (
        <div style={s.card}>
          <h3 style={s.cardTitle}><Mail size={16} /> Email History</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>
            Recent emails sent to this user.
          </p>
          {userData?.email_logs && userData.email_logs.length > 0 ? (
            userData.email_logs.map((log, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--border-primary)', fontSize: 13 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ 
                    padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600, textTransform: 'capitalize',
                    background: log.email_type === 'verification' ? 'var(--info-bg)' :
                               log.email_type === 'password_reset' ? 'var(--warning-bg)' :
                               log.email_type === 'cancellation' ? 'var(--error-bg)' :
                               log.email_type === 'first_purchase' ? 'var(--success-bg)' : 'var(--bg-tertiary)',
                    color: log.email_type === 'verification' ? 'var(--info)' :
                          log.email_type === 'password_reset' ? 'var(--warning)' :
                          log.email_type === 'cancellation' ? 'var(--error)' :
                          log.email_type === 'first_purchase' ? 'var(--success)' : 'var(--text-secondary)',
                  }}>
                    {log.email_type?.replace(/_/g, ' ') || 'email'}
                  </span>
                </div>
                <span style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>
                  {log.created_at ? new Date(log.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ', '}
                </span>
              </div>
            ))
          ) : (
            <p className="billing-empty">No emails sent yet.</p>
          )}
        </div>
      )}


    </div>
  );
}


// ================================================================
// SMALL COMPONENTS
// ================================================================

function MiniStat({ icon, label, value, color }) {
  return (
    <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-md)', textAlign: 'center' }}>
      <div style={{ color, marginBottom: 4, display: 'flex', justifyContent: 'center' }}>{icon}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-primary)' }}>{value}</div>
      <div style={{ color: 'var(--text-secondary)', fontSize: 10, marginTop: 2 }}>{label}</div>
    </div>
  );
}

function UsageCard({ label, used, limit, color }) {
  const pct = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
  const isUnlimited = limit >= 999999;
  return (
    <div style={{ background: 'var(--bg-tertiary)', borderRadius: 'var(--radius-md)', padding: 'var(--space-lg)', border: '1px solid var(--border-primary)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{label}</span>
        <span style={{ color, fontSize: 13, fontWeight: 700 }}>{used} / {isUnlimited ? '∞' : limit}</span>
      </div>
      {!isUnlimited && (
        <div style={{ background: 'var(--border-primary)', borderRadius: 4, height: 6, overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4 }} />
        </div>
      )}
    </div>
  );
}

function DetailRow({ icon, label, value, copyable }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border-primary)', flexWrap: 'wrap', gap: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-secondary)', fontSize: 13 }}>{icon}<span>{label}</span></div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 500 }}>{value || ', '}</span>
        {copyable && value && (
          <button onClick={() => { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 2000); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: copied ? 'var(--success)' : 'var(--text-tertiary)' }}>
            {copied ? <Check size={12} /> : <Copy size={12} />}
          </button>
        )}
      </div>
    </div>
  );
}

function Badge({ bg, color, children, style }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 'var(--radius-full)', fontSize: 11, fontWeight: 600, background: bg, color, ...style }}>
      {children}
    </span>
  );
}

function InputField({ label, value, onChange }) {
  return (
    <div>
      <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600, marginBottom: 4, textTransform: 'uppercase' }}>{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} style={{ width: '100%', padding: '8px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-primary)', background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 13, outline: 'none' }} />
    </div>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <div>
      <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600, marginBottom: 4, textTransform: 'uppercase' }}>{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} style={{ width: '100%', padding: '8px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-primary)', background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', cursor: 'pointer' }}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function ActionChip({ icon, label, color, onClick, loading, disabled }) {
  return (
    <button onClick={onClick} disabled={loading || disabled} style={{
      display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px',
      borderRadius: 'var(--radius-md)', border: `1px solid ${color}30`,
      background: loading ? `${color}10` : 'transparent', color: color,
      cursor: loading ? 'wait' : 'pointer', fontSize: 12, fontWeight: 600,
      whiteSpace: 'nowrap', opacity: disabled ? 0.4 : loading ? 0.6 : 1,
      transition: 'all 0.15s ease',
    }}>
      {loading ? (
        <span style={{ width: 13, height: 13, border: `2px solid ${color}30`, borderTop: `2px solid ${color}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} />
      ) : (
        icon
      )}
      {label}
    </button>
  );
}

// ================================================================
// STYLES
// ================================================================

const s = {
  backBtn: { background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, padding: 0 },
  card: { background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-xl)', padding: 'var(--space-xl)', boxShadow: 'var(--shadow-sm)', marginBottom: 'var(--space-xl)', minWidth: 0 },
  cardGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 'var(--space-lg)' },
  cardTitle: { color: 'var(--text-primary)', fontSize: 15, fontWeight: 600, margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 12, borderBottom: '1px solid var(--border-primary)' },
  avatar: { width: 72, height: 72, borderRadius: 'var(--radius-xl)', background: 'var(--accent-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 28, fontWeight: 800, flexShrink: 0 },
  infoRow: { color: 'var(--text-secondary)', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 'var(--space-sm)', marginBottom: 'var(--space-xl)' },
  statBox: { background: 'var(--bg-tertiary)', borderRadius: 'var(--radius-md)', padding: 'var(--space-md)', textAlign: 'center', border: '1px solid var(--border-primary)' },
  tabBar: { display: 'flex', gap: 4, marginBottom: 'var(--space-xl)', borderBottom: '1px solid var(--border-primary)', paddingBottom: 'var(--space-sm)', overflowX: 'auto' },
  tab: (active) => ({ display: 'flex', alignItems: 'center', gap: 5, padding: '8px 14px', borderRadius: 'var(--radius-md)', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0, background: active ? 'var(--accent-glow)' : 'transparent', color: active ? 'var(--accent-primary)' : 'var(--text-secondary)', borderBottom: active ? '2px solid var(--accent-primary)' : '2px solid transparent' }),
  textarea: { width: '100%', padding: 12, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-primary)', background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', resize: 'vertical', fontFamily: 'inherit' },
  saveBtn: { padding: '10px 20px', borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--accent-gradient)', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 700 },
  deleteNoteBtn: { background: 'none', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', padding: 4 },
  cancelBtn: { flex: 1, padding: '12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-primary)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 14, fontWeight: 600 },
  deleteBtn: { flex: 1, padding: '12px', borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--error)', color: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 700 },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 },
  modalContent: { background: 'var(--bg-secondary)', borderRadius: 20, padding: 'var(--space-xl)', maxWidth: 440, width: '100%', border: '1px solid var(--border-primary)', boxShadow: 'var(--shadow-xl)' },
};